import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import {
  Box, Stack, Drawer, CircularProgress, Button, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';
// hooks
import useResponsive from '../../../hooks/useResponsive';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { NAVBAR } from '../../../config';
// components
import Logo from '../../../components/Logo';
import Scrollbar from '../../../components/Scrollbar';
import { NavSectionVertical } from '../../../components/nav-section';
//
import navConfig from './NavConfig';
import { getWedgeNav } from './WedgeNavConfig';
import NavbarDocs from './NavbarDocs';
import NavbarUpgradeBanner from './NavbarUpgradeBanner';
import NavbarAccount from './NavbarAccount';
import NavbarFlyout from './NavbarFlyout';
import CollapseButton from './CollapseButton';
import { NavFlyoutProvider } from '../../../hooks/useNavFlyout';
import { isItemVisible } from '../../../guards/checkNavAccess';
import useAuth from '../../../hooks/useAuth';
import useNavBadges from '../../../hooks/useNavBadges';
import applyNavBadges from './applyNavBadges';
import axios from '../../../utils/axios';
import { getFullDomain } from '../../../utils/fullDomain';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
  const theme = useTheme();

  const { pathname } = useLocation();
  const { user, logout, hasPermission } = useAuth();
  const navBadges = useNavBadges();
  // Set when the user presses Back inside a flyout panel: it means "show me
  // the main menu again" without leaving the page they are on. Cleared as soon
  // as they navigate anywhere, because by then the URL has an opinion again.
  const [flyoutDismissed, setFlyoutDismissed] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [campusInfo, setCampusInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fullDomain = getFullDomain();
  const navigate = useNavigate();

  const updateFavicon = (faviconUrl) => {
    if (!faviconUrl) return;

    const favicons = document.querySelectorAll("link[rel*='icon']");
    favicons.forEach((favicon) => {
      favicon.href = faviconUrl;
    });

    if (favicons.length === 0) {
      const newFavicon = document.createElement('link');
      newFavicon.rel = 'icon';
      newFavicon.href = faviconUrl;
      document.head.appendChild(newFavicon);
    }
  };

  const isDesktop = useResponsive('up', 'lg');

  // The mobile drawer's contents are the full permission-filtered nav tree.
  // Latch it in on the first open and keep it mounted from then on: opens stay
  // instant, but a session that never touches the drawer never pays for it.
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  useEffect(() => {
    if (isOpenSidebar && !hasOpenedOnce) setHasOpenedOnce(true);
  }, [isOpenSidebar, hasOpenedOnce]);
  const storedCampusInfo = JSON.parse(localStorage.getItem('campusInfo'));

  const getCampusInfo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/accounts/whitelabel/info/${fullDomain}/`);

      if (response.data && Object.keys(response.data).length > 0) {
        setCampusInfo(response.data);
        // localStorage.setItem('campusInfo', JSON.stringify(response.data));
        updateFavicon(response.data.favicon);
      }
    } catch (err) {
      // if (err.error === 'Invalid slug') {
      //   navigate('/unknown', { replace: true });
      // }
      setError(err);
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const cached = localStorage.getItem('campusInfo');
    if (cached) {
      const parsedCache = JSON.parse(cached);
      setCampusInfo(parsedCache);
      updateFavicon(parsedCache.favicon);
      setLoading(false);
    }

    getCampusInfo();
  }, []);

  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    setFlyoutDismissed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  let navList;

  if (user?.role === 'org') {
    // A free-wedge org gets the reduced console instead of the full platform.
    // Falls through to the full nav for every other org, including one that
    // upgrades: plan_kind is cleared and the whole product appears.
    navList = getWedgeNav(user?.planKind) || navConfig[0].items;
  }
  else if (user?.role === 'user') {
    const hasActivePlan = user?.userPlan && user.userPlan.length > 0;
    navList = navConfig[1].items.map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (item.requiresActivePlan) return hasActivePlan;
        return true;
      }),
    }));
  }
  // else if (user?.role === 'cik') {
  //   navList = navConfig[2].items;
  // }
  else if (user?.role === 'restaurant_admin') {
    navList = navConfig[2].items;
  } else if (user?.role === 'chef') {
    navList = navConfig[3].items;
  } else if (user?.role === 'boss') {
    // Single shared boss nav; super_admin-only items are gated via `allowed` on the nav items.
    navList = navConfig[4].items;
  } else {
    navList = [];
  }

  // Counts for anything a colleague fills and this user is meant to act on.
  navList = applyNavBadges(navList, navBadges);

  // Which module owns the second panel right now. Derived from the URL, so a
  // deep link or a refresh lands with the right panel already open and no
  // state to restore. Icon-collapse mode opts out: there is no room for two
  // panels, and the module falls back to unfolding inline.
  const flyoutItems = navList
    .flatMap((group) => group.items || [])
    .filter((item) => item.flyout && isItemVisible(item, { user, hasPermission }));
  const activeFlyout =
    isDesktop && !isCollapse && !flyoutDismissed
      ? flyoutItems.find(
          (item) =>
            item.path && (pathname === item.path || pathname.startsWith(`${item.path}/`))
        )
      : undefined;
  const flyoutOpen = !!activeFlyout;
  // The main nav goes to its icon rail while the panel is up. This is local to
  // the render, not the global collapse setting: pressing Back restores the
  // full menu without having changed anything the user chose.
  const navCollapsed = isCollapse || flyoutOpen;

  const LogoSection = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: navCollapsed ? 48 : 80,
            width: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (user?.orgLogo) {
      return (
        <Box
          sx={{
            width: '100%',
            height: navCollapsed ? 48 : 80,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 1,
          }}
        >
          <img
            src={user.orgLogo}
            alt="Organization Logo"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
              transition: 'all 0.3s ease',
              minWidth: navCollapsed ? '32px' : '120px',
              minHeight: navCollapsed ? '32px' : '80px',
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              // Create a fallback div with the default Logo
              const fallback = document.createElement('div');
              fallback.style.cssText =
                'display: flex; justify-content: center; align-items: center; width: 100%; height: 100%;';
              fallback.innerHTML = 'LOGO'; // You might want to render your Logo component differently
              e.target.parentNode.appendChild(fallback);
            }}
          />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: '100%',
          height: navCollapsed ? 48 : 80,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Logo />
      </Box>
    );
  };

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Stack
        spacing={1}
        sx={{
          pt: 1,
          pl: navCollapsed ? 0.5 : 1.25,
          pr: navCollapsed ? 0.5 : 1.5,
          flexShrink: 0,
          ...(navCollapsed && { alignItems: 'center' }),
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={['org', 'restaurant_admin'].includes(user?.role) && isDesktop && !navCollapsed ? 'space-between' : 'center'}
          spacing={1}
          sx={{
            minHeight: navCollapsed ? 48 : 80,
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: ['org', 'restaurant_admin'].includes(user?.role) && isDesktop && !navCollapsed ? '1 1 auto' : 'none',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: 0,
              height: '100%',
              width: ['org', 'restaurant_admin'].includes(user?.role) && isDesktop && !navCollapsed ? 'calc(100% - 60px)' : '100%',
            }}
          >
            <LogoSection />
          </Box>

          {['org', 'restaurant_admin'].includes(user?.role) && isDesktop && !navCollapsed && (
            <Box
              sx={{
                flexShrink: 0,
                ml: 1,
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                pt: 1,
              }}
            >
              <CollapseButton onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
            </Box>
          )}
        </Stack>
      </Stack>

      <NavSectionVertical navConfig={navList} isCollapse={navCollapsed} />

      <Box sx={{ flexGrow: 1 }} />

      {!navCollapsed && user.accountStatus === 'prospect' && !user.hasSignedUpOnVP && <NavbarUpgradeBanner />}

      {/* Logout — mobile sidebar only; desktop has AccountPopover in the header */}
      {!isDesktop && (
        <Box sx={{ px: 1, pb: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Button
            fullWidth
            variant="outlined"
            color="error"
            size="medium"
            onClick={() => setConfirmLogout(true)}
            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
          >
            Log out
          </Button>
        </Box>
      )}

      {/* Logout confirmation dialog */}
      <Dialog
        open={confirmLogout}
        onClose={() => setConfirmLogout(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
      >
        <DialogTitle sx={{ pb: 1, pt: 3, fontWeight: 700 }}>
          Log out?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You'll need to sign in again to access your account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setConfirmLogout(false)}
            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="error"
            onClick={logout}
            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 700 }}
          >
            Log out
          </Button>
        </DialogActions>
      </Dialog>
    </Scrollbar>
  );

  return (
    <NavFlyoutProvider
      value={{
        // Clicking the entry re-opens the panel even when the click does not
        // change the URL, which is exactly the case after pressing Back while
        // sitting on the module root.
        onEnterFlyout: () => setFlyoutDismissed(false),
        activeFlyoutPath: activeFlyout?.path || null,
      }}
    >
    <RootStyle
      sx={{
        width: {
          // navCollapsed is true while a flyout is open, but the footprint is
          // not: rail plus panel add back up to the full width, which is what
          // keeps the header and the page still.
          lg: navCollapsed && !flyoutOpen
            ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH
            : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          // keepMounted avoids the mount/unmount cycle on every open, which was
          // the main source of jank in WebView. It only kicks in after the first
          // open (see hasOpenedOnce) so the whole nav tree isn't sitting in the
          // DOM, re-rendering, for a session that never opens the drawer.
          ModalProps={{ keepMounted: hasOpenedOnce }}
          transitionDuration={{ enter: 220, exit: 180 }}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              // GPU-accelerated slide: promote to composite layer before animation starts
              willChange: 'transform',
              transition: 'transform 220ms cubic-bezier(0.4, 0, 0.2, 1) !important',
            },
          }}
          sx={{
            '& .MuiBackdrop-root': {
              willChange: 'opacity',
              transition: 'opacity 220ms cubic-bezier(0.4, 0, 0.2, 1) !important',
            },
          }}
        >
          {hasOpenedOnce ? renderContent : null}
        </Drawer>
      )}

      {/* The module panel. A separate persistent drawer rather than part of the
          main one so the two slide independently and the main nav can shrink
          under it without its contents reflowing twice. */}
      {isDesktop && flyoutOpen && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              left: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              width: NAVBAR.DASHBOARD_FLYOUT_WIDTH,
              borderRightWidth: '1px',
              borderRightStyle: 'solid',
              borderRightColor: (theme) => theme.palette.divider,
              bgcolor: 'background.default',
            },
          }}
        >
          <NavbarFlyout item={activeFlyout} onBack={() => setFlyoutDismissed(true)} />
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              borderRightWidth: '1px',
              borderRightStyle: 'solid',
              borderRightColor: (theme) => theme.palette.divider,
              bgcolor: 'background.default',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.standard,
                }),
              ...(navCollapsed && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme) => theme.customShadows.z24,
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
    </NavFlyoutProvider>
  );
}
